import { pdkAxios } from '@/lib/pdk-axios';
import { dealStore, vehicleLocationStore } from '@/models/stores/index';
import type { VehicleLocation } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const useVehicleLocationMutations = ({
  dealId,
  dealShortId,
  onUpdateSuccess,
  onUpdateError,
  onCreateSuccess,
  onCreateError,
}: {
  dealId: string | undefined | null;
  dealShortId: string | undefined;
  onUpdateSuccess?: (data: null, variables: { locationId: string }) => void;
  onUpdateError?: (error: Error, variables: { locationId: string }) => void;
  onCreateSuccess?: (
    data: VehicleLocation,
    variables: { location: VehicleLocation },
  ) => void;
  onCreateError?: (
    error: Error,
    variables: { location: VehicleLocation },
  ) => void;
}) => {
  const queryClient = useQueryClient();

  const createVehicleLocationMutation = useMutation({
    onMutate: async ({ location }) => {
      vehicleLocationStore.add(location);
    },
    mutationFn: async ({ location }: { location: VehicleLocation }) => {
      const response = await pdkAxios
        .post(`/vehicle-locations/create`, {
          id: location.id,
          name: location.name,
        })
        .catch((e) => {
          console.error('Error creating vehicle location', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, { location }) => {
      onCreateSuccess?.(data, { location });

      // UX NOTE: we want to update the vehicle location immediately after it is created
      // so that the user can see the location on the deal immediately
      if (dealId) {
        updateVehicleCurrentLocationMutation.mutate({
          locationId: location.id,
        });
      }
    },
    onError: (error, variables) => {
      onCreateError?.(error, variables);
      console.error('Failed to create vehicle location', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-locations'] });
    },
  });

  const updateVehicleCurrentLocationMutation = useMutation({
    onMutate: async ({ locationId }) => {
      if (!dealId) {
        throw new Error(
          'updateVehicleCurrentLocationMutation: dealId is required',
        );
      }

      // Optimistically update the deal
      dealStore.updateVehicleCurrentLocation({ id: dealId, locationId });
    },
    mutationFn: async ({ locationId }: { locationId: string }) => {
      if (!dealId) {
        throw new Error(
          'updateVehicleCurrentLocationMutation: dealId is required',
        );
      }

      const response = await pdkAxios
        .post(`/deals/${dealId}/vehicle-current-location`, {
          vehicle_current_location_id: locationId,
        })
        .catch((e) => {
          console.error('Error updating vehicle current location', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, { locationId }) => {
      onUpdateSuccess?.(data, { locationId });
    },
    onError: (error, variables) => {
      onUpdateError?.(error, variables);
      console.error(
        'Failed to update vehicle current location',
        error,
        variables,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });

  return {
    createVehicleLocationMutation,
    updateVehicleCurrentLocationMutation,
  };
};

export default useVehicleLocationMutations;
